// NPM MODULES
import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import moment from 'moment/moment';

// OUR COMPONENTS
import Layout from '../../components/layouts/Layout';
import Loader from '../../components/common/Loader';
import CostCentreReportTable from '../../components/reports/costcentre/CostCentreReportTable';
import CostCentreReportTableRow from '../../components/reports/costcentre/CostCentreReportTableRow';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import UsageLog from '../../components/reports/costcentre/UsageLog';
import DataExportModal from '../../components/common/modals/DataExportModal';

// NETWORKING
import useCostCentreReportRequest from '../../requests/CostCentreReportRequest';

// ROUTES
import { ReportsRoutes } from '../../routes/ReportsRoutes';

// FORMATTED MESSAGES
import CostCentreReportsFormattedMessages from '../../messages/reports/CostCentreReportsFormattedMessages';

const CostCentreReports = ({
  customer,
  user,
  errorHandler,
  navigate,
  accessLevel,
  accessType,
}) => {
  const [smsList, setSmsList] = useState(null);
  const [showUsageLog, setShowUsageLog] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [activePage, setActivePage] = useState('list');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [usageLogAccess, setUsageLogAccess] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showEmailSentModal, setShowEmailSentModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [dataExists, setDataExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({
    Direct: 0,
    Standard: 0,
    Replies: 0,
    Sent: 0,
    Pending: 0,
    DirectParts: 0,
    StandardParts: 0,
    RepliesParts: 0,
    SentParts: 0,
    PendingParts: 0,
    Users: 0,
  });
  const { getAdministrator, getReportData, exportCostCentreReportData } =
    useCostCentreReportRequest();

  useEffect(() => {
    if (customer && customer !== null) {
      const fetchAdministrator = async () => {
        const admin = await getAdministrator(
          user.customer_contact_id,
          true,
          errorHandler
        );
        setUsageLogAccess(admin.usage_log_access_flag);
      };
      fetchAdministrator();
    }
  }, [customer, user, errorHandler]);

  const handleShowUsageLog = () => {
    setShowUsageLog(!showUsageLog);
  };

  const handleCostCentreSearch = async (fromDate, toDate, timeRange) => {
    setLoading(true);
    const searchParams = {
      fromDate: fromDate,
      toDate: toDate,
      timeRange: timeRange,
      timeRangeStart: fromDate,
      timeRangeEnd: toDate,
    };
    const result = await getReportData(searchParams, errorHandler);

    const sortedResults = sortList(result);

    console.log(`sorted Data: ${JSON.stringify(sortedResults, null, 2)}`);
    setFromDate(fromDate);
    setToDate(toDate);
    formatData(sortedResults);
  };

  useEffect(() => {
    if (fromDate && toDate) handleCostCentreSearch(fromDate, toDate);
  }, [fromDate, toDate]);

  const exportCostCentreData = async () => {
    setExporting(true);
    const searchParams = {
      fromDate: fromDate.toMillis(),
      toDate: toDate.toMillis(),
    };
    const data = await exportCostCentreReportData(searchParams, errorHandler);
    console.log('data:', data);
    setEmailRecipient(data);
    setExporting(false);
    setShowEmailSentModal(true);
  };

  const sortList = (data) => {
    return data.sort((a, b) => {
      if (a.cost_centre_name < b.cost_centre_name) {
        return -1;
      }
      if (a.cost_centre_name > b.cost_centre_name) {
        return 1;
      }
      return 0;
    });
  };

  const formatData = (data) => {
    try {
      const total = {
        Direct: 0,
        Standard: 0,
        Replies: 0,
        Sent: 0,
        Pending: 0,
        DirectParts: 0,
        StandardParts: 0,
        RepliesParts: 0,
        SentParts: 0,
        PendingParts: 0,
        Users: 0,
      };

      const formattedData = data.map((el, index) => (
        <CostCentreReportTableRow
          key={'key_' + index}
          id={0}
          name={el.cost_centre_name}
          userCount={el.userCount}
          Direct={{
            sent: el.direct_sent,
            parts: el.direct_parts,
          }}
          Standard={{
            sent: el.messages_sent,
            parts: el.messages_parts,
          }}
          Replies={{
            sent: el.replies_sent,
            parts: el.replies_parts,
          }}
          Sent={{
            sent: el.reminders_sent,
            parts: el.reminders_parts,
          }}
          Pending={{
            sent: el.reminders_pending,
            parts: el.reminders_pending_parts,
          }}
          onClickName={() =>
            navigate('/reports/user', {
              state: {
                stv: true,
                selectedName: el.cost_centre_name,
                selected: el.cost_centre_id,
                filtered: true,
              },
            })
          }
        />
      ));

      data.forEach((el) => {
        total.Direct += parseInt(el.direct_sent);
        total.Standard += parseInt(el.messages_sent);
        total.Replies += parseInt(el.replies_sent);
        total.Sent += parseInt(el.reminders_sent);
        total.Pending += parseInt(el.reminders_pending);
        total.Users += parseInt(el.userCount);
        total.DirectParts += parseInt(el.direct_parts);
        total.StandardParts += parseInt(el.messages_parts);
        total.RepliesParts += parseInt(el.replies_parts);
        total.SentParts += parseInt(el.reminders_parts);
        total.PendingParts += parseInt(el.reminders_pending_parts);
      });

      setSmsList(formattedData);
      setTotals(total);
      setLoading(false);
    } catch (e) {
      console.log('data error:', e);
    }
  };

  const handleNavigation = (target) => {
    window.location.href = '/reports/user?id=' + target;
  };

  const generateSearchTools = () => {
    return (
      <Box sx={{ marginBottom: { xs: 4, md: 2 } }}>
        <DateRangeSelector
          handleDateSearch={(fromDate, toDate, timeRange) =>
            handleCostCentreSearch(fromDate, toDate, timeRange)
          }
          parentStartDate={fromDate}
          setParentStartDate={setFromDate}
          parentEndDate={toDate}
          setParentEndDate={setToDate}
        />
      </Box>
    );
  };

  const changePage = (page, value) => {
    setActivePage(page);
    setSelectedData(value);
  };

  const generateUserContentLink = () => {
    if (accessLevel < '1' || accessType < '1') return null;

    return (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'end',
          marginBottom: 2,
        }}
      >
        {usageLogAccess === 'Y' && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleShowUsageLog}
          >
            User Message Content Report
          </Button>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={exportCostCentreData}
          disabled={exporting}
        >
          Email Report
        </Button>
      </Box>
    );
  };

  const reloadPage = () => {
    window.location.reload(1);
  };

  if (accessLevel <= '0' || accessType === '0') {
    return null;
  }

  return (
    <Layout
      routes={ReportsRoutes}
      title={
        showUsageLog
          ? CostCentreReportsFormattedMessages.usageLogTitle
          : CostCentreReportsFormattedMessages.costCentreReportsTitle
      }
      accessLevel={accessLevel}
      accessType={accessType}
      backButtonText={showUsageLog ? 'Back to Cost Centre Reports' : null}
      handleBackButton={showUsageLog ? handleShowUsageLog : null}
    >
      {showUsageLog ? (
        <UsageLog
          customer={customer}
          errorHandler={errorHandler}
          accessLevel={accessLevel}
          accessType={accessType}
        />
      ) : (
        <>
          <Typography gutterBottom>
            {CostCentreReportsFormattedMessages.accountText}{' '}
            {customer ? customer.customer_name : ''}
          </Typography>

          {generateSearchTools()}
          {loading ? (
            <Loader />
          ) : (
            <>
              {generateUserContentLink()}
              <CostCentreReportTable
                changePage={changePage}
                totals={totals}
                list={smsList}
              />
              <DataExportModal
                show={showEmailSentModal}
                onHide={() => setShowEmailSentModal(!showEmailSentModal)}
                emailRecipient={emailRecipient}
              />
            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default CostCentreReports;
